import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Link } from "@/i18n/LocaleLink";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useSeo } from "@/hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  en: {
    title: "Payment received",
    waiting: "Confirming your payment…",
    heading: "You're booked.",
    body: (name) => name
      ? `Thank you, ${name}. Your place is reserved — a personal confirmation email from Irida is on its way to your inbox with everything you need to know.`
      : "Thank you. Your place is reserved — a personal confirmation email from Irida is on its way to your inbox with everything you need to know.",
    dateLabel: "Workshop",
    home: "Back to Women's Den",
    failed: "We couldn't confirm this payment. If you believe you paid, please contact us and we'll sort it out together.",
    contact: "Get in touch",
    programme: {
      heading: "You're in.",
      body: "Thank you — your place in the four-month programme is confirmed. Irida will be in touch with you personally to schedule your first session, and a receipt from Stripe is on its way to your inbox.",
      label: "Programme",
      topic: "Perimenopause & Menopause 1:1",
      detail: "4 × 90 minutes · over four months",
    },
  },
  nl: {
    title: "Betaling ontvangen",
    waiting: "Je betaling wordt bevestigd…",
    heading: "Je plek is gereserveerd.",
    body: (name) => name
      ? `Dank je wel, ${name}. Je plek is gereserveerd — een persoonlijke bevestigingsmail van Irida is onderweg naar je inbox met alles wat je moet weten.`
      : "Dank je wel. Je plek is gereserveerd — een persoonlijke bevestigingsmail van Irida is onderweg naar je inbox met alles wat je moet weten.",
    dateLabel: "Workshop",
    home: "Terug naar Women's Den",
    failed: "We konden deze betaling niet bevestigen. Als je denkt dat je betaald hebt, neem dan contact op — dan lossen we het samen op.",
    contact: "Neem contact op",
    programme: {
      heading: "Je bent erbij.",
      body: "Dank je wel — je plek in het programma van vier maanden is bevestigd. Irida neemt persoonlijk contact met je op om de eerste sessie te plannen, en een betaalbewijs van Stripe is onderweg naar je inbox.",
      label: "Programma",
      topic: "Perimenopauze & Menopauze 1-op-1",
      detail: "4 × 90 minuten · verspreid over vier maanden",
    },
  },
};

export default function PaymentSuccess() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [record, setRecord] = useState(null);
  const [gaveUp, setGaveUp] = useState(false);
  useSeo({ title: `${c.title} | Women's Den`, description: c.heading, noindex: true });

  useEffect(() => {
    if (!sessionId) { setGaveUp(true); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await fetch(`${API}/bookings/status/${sessionId}`);
        if (res.ok) {
          const data = await res.json();
          if (data.payment_status === "paid") { setRecord({ ...data, kind: "booking" }); return; }
        } else if (res.status === 404) {
          // Not a workshop booking — this may be a menopause programme purchase
          const pres = await fetch(`${API}/programme/status/${sessionId}`);
          if (pres.ok) {
            const pdata = await pres.json();
            if (pdata.payment_status === "paid") { setRecord({ kind: "programme", payment_status: "paid" }); return; }
          }
        }
      } catch { /* retry */ }
      attempts += 1;
      if (attempts < 15) setTimeout(poll, 2000);
      else setGaveUp(true);
    };
    poll();
  }, [sessionId]);

  const dateLabel = record?.date
    ? new Intl.DateTimeFormat(lang === "nl" ? "nl-NL" : "en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(new Date(`${record.date}T12:00:00`))
    : null;

  return (
    <main className="bg-ink min-h-screen grain relative flex items-center" data-testid="payment-success-page">
      <div className="mx-auto max-w-2xl px-6 py-32 text-center relative z-10 w-full">
        {!record && !gaveUp && (
          <p className="font-serif italic text-2xl text-sand animate-pulse" data-testid="payment-waiting">{c.waiting}</p>
        )}
        {gaveUp && !record && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-base leading-relaxed text-cream/85" data-testid="payment-unconfirmed">{c.failed}</p>
            <Link to="/contact" data-testid="payment-failed-contact"
              className="group mt-8 inline-flex items-center gap-2 text-sm text-sand hover:text-cream transition-colors duration-300">
              {c.contact}
              <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>
        )}
        {record && record.kind === "programme" && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-cream" data-testid="payment-confirmed-heading">{c.programme.heading}</h1>
            <p className="mt-8 text-base leading-relaxed text-sand/90">{c.programme.body}</p>
            <div className="mt-10 inline-block border border-clay/40 px-10 py-6" data-testid="payment-booking-details">
              <p className="text-xs tracking-[0.3em] uppercase text-sand/70">{c.programme.label}</p>
              <p className="mt-3 font-serif text-2xl text-cream" data-testid="payment-topic">{c.programme.topic}</p>
              <p className="mt-1 text-sm text-sand/80" data-testid="payment-date">{c.programme.detail}</p>
            </div>
            <div>
              <Link to="/" data-testid="payment-home-link"
                className="group mt-12 inline-flex items-center gap-2 text-sm text-sand hover:text-cream transition-colors duration-300">
                {c.home}
                <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </motion.div>
        )}
        {record && record.kind !== "programme" && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-cream" data-testid="payment-confirmed-heading">{c.heading}</h1>
            <p className="mt-8 text-base leading-relaxed text-sand/90">{c.body((record.name || "").trim().split(" ")[0] || "")}</p>
            <div className="mt-10 inline-block border border-clay/40 px-10 py-6" data-testid="payment-booking-details">
              <p className="text-xs tracking-[0.3em] uppercase text-sand/70">{c.dateLabel}</p>
              <p className="mt-3 font-serif text-2xl text-cream" data-testid="payment-topic">{record.topic}</p>
              <p className="mt-1 text-sm text-sand/80" data-testid="payment-date">{dateLabel}</p>
            </div>
            <div>
              <Link to="/" data-testid="payment-home-link"
                className="group mt-12 inline-flex items-center gap-2 text-sm text-sand hover:text-cream transition-colors duration-300">
                {c.home}
                <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </main>
  );
}
